import React from "react";
import Table from "./Table";
import { FWaveRowType } from "../types/NCS_EMG";

interface FWaveTableProps {
  rows: FWaveRowType[];
  setRows: React.Dispatch<React.SetStateAction<FWaveRowType[]>>;
  nerveOptions: string[];
  stimOptions: string[];
  recOptions: string[];
  sideOptions: string[];
}

const F_WAVE_MAP: Record<
  "UEx" | "LEx",
  Array<{ label: string; nerve: string; stim: string; rec: string }>
> = {
  UEx: [
    { label: "Median - Wrist", nerve: "Median", stim: "Wrist", rec: "APB" },
    { label: "Ulnar - Wrist", nerve: "Ulnar", stim: "Wrist", rec: "ADM" },
  ],
  LEx: [
    { label: "Tibial - Ankle", nerve: "Tibial", stim: "Ankle", rec: "AH" },
    { label: "Peroneal - Ankle", nerve: "Peroneal", stim: "Ankle", rec: "EDB" },
  ],
};

const headers = ["Nerve", "Side", "Stim", "Rec", "Lat(ms)", "Note"];

const FWaveTable: React.FC<FWaveTableProps> = ({
  rows,
  setRows,
  nerveOptions,
  stimOptions,
  recOptions,
  sideOptions,
}) => {
  const addStimRows = (label: string) => {
    const selected =
      F_WAVE_MAP.UEx.find((item) => item.label === label) ||
      F_WAVE_MAP.LEx.find((item) => item.label === label);

    if (!selected) return;

    const newPair: FWaveRowType[] = sideOptions.map((side) => ({
      Nerve: selected.nerve,
      Side: side,
      Stim: selected.stim,
      Rec: selected.rec,
      "Lat(ms)": "",
      Note: "",
    }));

    setRows((prev) => {
      const updated = [...prev];
      let filled = 0;

      for (let index = 0; index < updated.length && filled < newPair.length; index++) {
        const row = updated[index];
        const isEmpty = Object.values(row).every((value) => !value || value.trim() === "");
        if (isEmpty) {
          updated[index] = { ...updated[index], ...newPair[filled++] };
        }
      }

      if (filled < newPair.length) {
        return [...updated, ...newPair.slice(filled)];
      }

      return updated;
    });
  };

  return (
    <section className="section-block">
      <div className="section-header">
        <h2 className="section-title">F-wave</h2>

        <div className="toolbar">
          {(["UEx", "LEx"] as const).map((region) => (
            <select
              key={region}
              className="app-select compact-select"
              onChange={(event) => {
                addStimRows(event.target.value);
                event.target.value = "";
              }}
            >
              <option value="">{`+ Add Nerve (${region})`}</option>
              {F_WAVE_MAP[region].map((item) => (
                <option key={item.label} value={item.label}>
                  {item.label}
                </option>
              ))}
            </select>
          ))}
        </div>
      </div>

      <Table
        title=""
        headers={headers}
        rows={rows}
        setRows={setRows}
        nerveOptions={nerveOptions}
        sideOptions={sideOptions}
        stimOptions={stimOptions}
        recOptions={recOptions}
        muscleOptions={[]}
        dropdownOptions={{ Note: ["Absent", "Prolonged"] }}
      />
    </section>
  );
};

export default FWaveTable;
